import clsx from "clsx"
import { useEffect, useRef, useState } from "react"
import { DayPicker as ReactDayPicker } from "react-day-picker"
import { useForm } from "react-hook-form"

import useIsDesktop from "@hooks/useIsDesktop"

import { ifSpaceOrEnter } from "@utils/keyboard"

import { Divider } from "@atoms"

import { Popover } from "@molecules"
import {
    SHARED_CLASS_NAMES,
    SHARED_COMPONENTS,
    SHARED_DAY_PICKER_PROPS,
} from "@molecules/DatePicker/DatePicker.constants"
import { useDatePickerOpenState } from "@molecules/DatePicker/DatePicker.hooks"
import parentStyles from "@molecules/DatePicker/DatePicker.module.scss"
import { formatSingleDate } from "@molecules/DatePicker/DatePicker.utils"
import { Form } from "@molecules/Form/Form"
import DateInput from "@molecules/Form/inputs/DateInput/DateInput"

import styles from "./DatePickerSingle.module.scss"
import { DatePickerSingleProps } from "./DatePickerSingle.types"

interface SingleFormData {
    dateInput: string
}

export default function DatePickerSingle(props: DatePickerSingleProps) {
    const {
        className,
        classNames,
        showOutsideDays = true,
        children,
        open: controlledOpen,
        onOpenChange = () => null,
        onSelect,
        defaultSelected,
        selected,
        ...rest
    } = props

    const [isOpen, setIsOpen] = useDatePickerOpenState({ controlledOpen, onOpenChange })

    const initialDate = selected ?? defaultSelected

    const [parsedDate, setParsedDate] = useState<Date | undefined>(initialDate)
    const [month, setMonth] = useState<Date | undefined>(initialDate)
    const [tempParsedDate, setTempParsedDate] = useState<Date | null>(initialDate ?? null)

    const isDesktop = useIsDesktop()

    // Sync internal state when controlled `selected` changes. Skip the initial mount so
    // uncontrolled usage (`defaultSelected` only) isn't overwritten with `undefined`.
    const hasMountedSelectedSyncRef = useRef(false)
    useEffect(() => {
        if (!hasMountedSelectedSyncRef.current) {
            hasMountedSelectedSyncRef.current = true
            return
        }
        if (selected) {
            setParsedDate(selected)
            setMonth(selected)
        } else {
            // Clear the selection and show the current month when cleared
            setParsedDate(undefined)
            setMonth(new Date())
        }
    }, [selected])

    const defaultInputValue = initialDate ? formatSingleDate(initialDate) : ""

    const form = useForm<SingleFormData>({
        defaultValues: {
            dateInput: defaultInputValue,
        },
    })

    const handleDateSelect = (date: Date | undefined) => {
        onSelect(date)
        setIsOpen(false)
    }

    const onInputDateChange = (date: Date | null) => {
        setTempParsedDate(date)
        if (date) {
            setParsedDate(date)
            setMonth(date)
        } else {
            // Clear the selection and show the current month when cleared
            setParsedDate(undefined)
            setMonth(new Date())
        }
    }

    const onApplyButtonClick = () => {
        if (tempParsedDate) {
            setParsedDate(tempParsedDate)
            setMonth(tempParsedDate)
        }
        handleDateSelect(tempParsedDate ?? undefined)
    }

    const onCalendarSelect = (date: Date | undefined) => {
        setParsedDate(date)
        if (date) {
            setMonth(date)
            setTempParsedDate(date)
            form.setValue("dateInput", formatSingleDate(date))
        }

        if (isDesktop) {
            handleDateSelect(date)
        }
    }

    const triggerRef = useRef<HTMLDivElement>(null)

    const focusInnerInput = () => {
        triggerRef.current?.querySelector("input")?.focus()
    }

    return (
        <Popover.Root isOpen={isOpen} onOpenChange={setIsOpen}>
            <Popover.Trigger>
                <div
                    ref={triggerRef}
                    tabIndex={0}
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-haspopup="dialog"
                    className={styles.trigger}
                    onClick={(event) => {
                        // Always open on click, prevent toggle-close behavior
                        if (isOpen) {
                            event.preventDefault()
                        }
                    }}
                    onKeyDown={(event) => {
                        const isFromTrigger = event.target === event.currentTarget
                        const isFromInput = (event.target as HTMLElement).tagName === "INPUT"

                        // Activate from the wrapper: open the picker and move focus into the input.
                        if (!isOpen && isFromTrigger) {
                            ifSpaceOrEnter(event, () => {
                                setIsOpen(true)
                                focusInnerInput()
                            })
                            return
                        }

                        // Select the typed date and close the picker on Enter key press.
                        // Only handle Enter from the input itself, not from buttons (like clear).
                        const isKeyEnter = ["Enter", "NumpadEnter"].includes(event.code)
                        if (isKeyEnter && isOpen && isFromInput) {
                            handleDateSelect(parsedDate)
                            if (document.activeElement instanceof HTMLElement) {
                                document.activeElement.blur()
                            }
                        }
                    }}
                >
                    {children}
                </div>
            </Popover.Trigger>

            <Popover.Viewport
                align="start"
                className={parentStyles.popover}
                onOpenAutoFocus={(event) => {
                    // Prevent the popover from stealing focus from the input
                    event.preventDefault()
                }}
            >
                <Popover.Header.Root />
                <div className={parentStyles.base}>
                    {/* Prevent mousedown from stealing focus so the trigger input stays focused while clicking in the picker */}
                    <div className={parentStyles.calendar} onMouseDown={(event) => event.preventDefault()}>
                        <ReactDayPicker
                            {...rest}
                            {...SHARED_DAY_PICKER_PROPS}
                            showOutsideDays={showOutsideDays}
                            className={clsx(parentStyles.picker, className)}
                            selected={parsedDate}
                            month={month}
                            mode="single"
                            required={true}
                            onMonthChange={setMonth}
                            onSelect={onCalendarSelect}
                            classNames={{
                                ...SHARED_CLASS_NAMES,
                                ...classNames,
                            }}
                            components={SHARED_COMPONENTS}
                        />
                    </div>

                    {!isDesktop && (
                        <>
                            <Divider />
                            <div className={styles.mobileInput}>
                                <Form form={form}>
                                    <DateInput
                                        name="dateInput"
                                        size="md"
                                        label="Date"
                                        onDateChange={onInputDateChange}
                                        placeholder="Enter date manually..."
                                        isDisabled={false}
                                        isControlled={true}
                                        button={{
                                            variant: "solid",
                                            onClick: onApplyButtonClick,
                                            children: "Apply",
                                        }}
                                    />
                                </Form>
                            </div>
                        </>
                    )}
                </div>
            </Popover.Viewport>
        </Popover.Root>
    )
}
